import React from 'react';
import { Spin, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { ActividadCliente, ActividadReciente, ClaseActividad, ValorCampo } from '../../api/feriasApi';
import { FctAviso } from '../../fct/react';
import { CAMPOS } from './campos';
import { dia, fechaHoraMadrid, SIN_DATO } from './textos';

/** Cómo se nombra cada clase de elemento en la lista (terminología de Oportunidades: «oportunidad de venta»). */
const CLASES: Record<ClaseActividad, string> = {
  ACTIVIDAD: 'Actividad',
  VENTA: 'Oportunidad de venta',
  COMPRA: 'Oportunidad de compra',
};

// Las indicaciones marcadas de la ficha verificada, con las etiquetas de la verificación (campos.ts).
function indicacionesDe(valores: Record<string, ValorCampo>): string[] {
  const opciones = new Map(CAMPOS.find((c) => c.clave === 'tratamiento')?.opciones ?? []);
  const marcadas = Array.isArray(valores.tratamiento) ? (valores.tratamiento as string[]) : [];
  const indicaciones = marcadas.map((valor) => opciones.get(valor) ?? valor);
  if (valores.nueva_ficha_epc === true) {
    indicaciones.push('Generar nueva ficha EPC');
  }
  return indicaciones;
}

const COLUMNAS: ColumnsType<ActividadCliente> = [
  { title: 'Qué', dataIndex: 'clase', render: (clase: ClaseActividad) => CLASES[clase] },
  { title: 'Tipo', dataIndex: 'tipo' },
  {
    title: 'Número',
    key: 'numero',
    className: 'fct-mono',
    render: (_, elemento) => (elemento.clase === 'ACTIVIDAD' ? SIN_DATO : elemento.id),
  },
  { title: 'Fecha', dataIndex: 'fecha', className: 'fct-num', render: (fecha: string) => fechaHoraMadrid(fecha) },
  { title: 'Estado', dataIndex: 'estado' },
  { title: 'Responsable', dataIndex: 'responsable', render: (responsable: string | null) => responsable ?? SIN_DATO },
  { title: 'Origen', dataIndex: 'origen', render: (origen: string | null) => origen ?? SIN_DATO },
];

/** Lo que muestra la sección; cada parte la carga «Acciones», y puede faltar o haber fallado por su cuenta. */
export interface DatosTratamiento {
  /** Lo verificado de la ficha: `undefined` mientras carga, `null` si no está verificada. */
  valores: Record<string, ValorCampo> | null | undefined;
  errorFicha: string | null;
  actividad: ActividadReciente | null;
  errorActividad: string | null;
}

/**
 * La sección Tratamiento de «Acciones» (M6 §3.1, puntos 1 y 2): las indicaciones que Marketing marcó en
 * la ficha, de lo verificado, y la actividad reciente del cliente en Gescom, desde la reunión menos 2
 * días hasta hoy. Las indicaciones orientan, pero no obligan (D-FE-37).
 */
export const TratamientoCaso: React.FC<DatosTratamiento & { anadir?: React.ReactNode }> = ({ valores, errorFicha, actividad, errorActividad, anadir }) => {

  const indicaciones = valores ? indicacionesDe(valores) : [];
  const notas = typeof valores?.notas_tratamiento === 'string' ? valores.notas_tratamiento.trim() : '';

  return (
    <>
      <h3 className="ferias-acciones__titulo">Indicaciones de la ficha</h3>
      {errorFicha && (
        <FctAviso tipo="error" titulo="No se han podido leer las indicaciones de la ficha">
          {errorFicha}
        </FctAviso>
      )}
      {!errorFicha && valores === undefined && <Spin aria-label="Cargando las indicaciones de la ficha" />}
      {valores === null && <p className="ferias-acciones__pendiente">La ficha aún no está verificada.</p>}
      {valores && indicaciones.length === 0 && notas === '' && (
        <p className="ferias-acciones__pendiente">La ficha no marca ninguna indicación de tratamiento.</p>
      )}
      {indicaciones.length > 0 && (
        <ul className="ferias-acciones__indicaciones" aria-label="Indicaciones marcadas">
          {indicaciones.map((indicacion) => (
            <li key={indicacion}>{indicacion}</li>
          ))}
        </ul>
      )}
      {notas !== '' && (
        <p>
          <b>Notas para el tratamiento:</b> {notas}
        </p>
      )}

      <h3 className="ferias-acciones__titulo">Actividad reciente del cliente</h3>
      {errorActividad && (
        <FctAviso tipo="error" titulo="No se ha podido leer la actividad del cliente">
          {errorActividad}
        </FctAviso>
      )}
      {!errorActividad && !actividad && <Spin aria-label="Cargando la actividad del cliente" />}
      {actividad && !actividad.disponible && (
        <FctAviso tipo="info" titulo="La actividad del cliente no está disponible">
          Esta base de datos no tiene los datos de Gescom que la muestran.
        </FctAviso>
      )}
      {actividad?.disponible && (
        <Table<ActividadCliente>
          size="small"
          aria-label="Actividad reciente del cliente"
          rowKey={(elemento) => `${elemento.clase}-${elemento.id}`}
          columns={COLUMNAS}
          dataSource={actividad.elementos}
          pagination={false}
          title={() => `Del ${dia(actividad.desde)} al ${dia(actividad.hasta)}`}
          locale={{ emptyText: 'Sin actividades ni oportunidades del cliente en esas fechas.' }}
        />
      )}
      {/* «Añadir actividad» (M6 §3.1, punto 3): la que se guarda aparece en la lista de arriba. */}
      {anadir && <div className="ferias-acciones__anadir">{anadir}</div>}
    </>
  );
};
